import React from 'react';
import { colors } from './theme';
import { getWarrantyRatioColor } from './warrantyUtils';

const API_BASE = process.env.REACT_APP_API_URL;

function BarChart({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count));
  return (
    <div style={{ padding: '0.5rem 0' }}>
      {data.map(item => (
        <div key={item._id} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span style={{ flex: '0 0 90px', color: colors.textMuted }}>{item._id}</span>
          <div style={{ flex: 1, backgroundColor: colors.surfaceMuted, borderRadius: '999px', overflow: 'hidden', marginRight: '0.5rem', border: `1px solid ${colors.panelBorder}` }}>
            <div style={{ height: '16px', width: `${(item.count / max) * 100}%`, backgroundColor: colors.accent, borderRadius: '10px' }} />
          </div>
          <span style={{ fontWeight: 'bold', color: colors.textStrong }}>{item.count}</span>
        </div>
      ))}
    </div>
  );
}

function DoughnutChart({ data, colorMap }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.count, 0);
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;
  return (
    <div style={{ position: 'relative', width: '100px', height: '100px' }}>
      <svg width="100" height="100" style={{ transform: 'rotate(-90deg)' }}>
        {data.map((d, idx) => {
          const dash = (d.count / total) * circumference;
          const dashArray = `${dash} ${circumference - dash}`;
          const circle = (
            <circle
              key={d._id}
              r={radius}
              cx="50"
              cy="50"
              fill="transparent"
              stroke={colorMap[d._id] || colors.accent}
              strokeWidth="20"
              strokeDasharray={dashArray}
              strokeDashoffset={-cumulative / total * circumference}
            />
          );
          cumulative += d.count;
          return circle;
        })}
      </svg>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontWeight: 'bold', color: colors.textStrong }}>
        {total}
      </div>
    </div>
  );
}

export default function RmaAnalyticsCard({ stats, warranty, year, setYear }) {
  if (!stats || !warranty) return null;

  const safeReturnReasonStats = Array.isArray(stats.byReturnReason)
    ? stats.byReturnReason.filter(item => item && item._id)
    : [];

  const STATUS_LABELS = {
    pending: 'Pending',
    assigned: 'Assigned',
    received: 'Received',
    awaiting_repair: 'Awaiting Repair',
    repairing: 'Repairing',
    awaiting_shipping: 'Awaiting Shipping',
    shipped: 'Shipped',
    archived: 'Archived'
  };

  const STATUS_ALIASES = {
    Pending: 'pending',
    Assigned: 'assigned',
    Received: 'received',
    'Awaiting Repair Start': 'awaiting_repair',
    Assessment: 'awaiting_repair',
    'Repair In Progress': 'repairing',
    Repairing: 'repairing',
    Ready: 'awaiting_shipping',
    'Repair Finished': 'awaiting_shipping',
    'Awaiting Shipping Details': 'awaiting_shipping',
    'Awaiting Dispatch Confirmation': 'awaiting_shipping',
    Shipped: 'shipped',
    ArchivedCaseClosed: 'archived',
    Closed: 'archived'
  };

  const statusColors = {
    pending: '#f1c40f',
    assigned: '#95a5a6',
    received: '#333333',
    awaiting_repair: '#9b59b6',
    repairing: '#FFC107',
    awaiting_shipping: '#00a8ff',
    shipped: '#d3d3d3',
    archived: '#7f8c8d'
  };

  const normalizeStatusKey = status => {
    if (!status) return 'pending';
    const alias = STATUS_ALIASES[status];
    if (alias) return alias;
    const lowered = typeof status === 'string' ? status.toLowerCase() : status;
    return STATUS_LABELS[lowered] ? lowered : lowered;
  };

  return (
    <div style={{ maxWidth: '880px', margin: '1rem auto', backgroundColor: colors.surface, borderRadius: '20px', boxShadow: colors.shadow, overflow: 'hidden', border: `1px solid ${colors.panelBorder}` }}>
      <div style={{ background: 'linear-gradient(135deg, rgba(70,187,137,0.18), rgba(70,187,137,0.08))', color: colors.textStrong, padding: '1rem 1.15rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', borderBottom: `1px solid rgba(70,187,137,0.18)` }}>
        <div>
          <div className="ni-eyebrow" style={{ marginBottom: '0.15rem' }}>Service Quality</div>
          <h2 style={{ margin: 0 }}>RMA Analytics</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.9rem', color: colors.textStrong }}>
            Year:
            <select value={year} onChange={e => setYear(e.target.value)} className="ni-select" style={{ marginLeft: '0.5rem', minWidth: '120px' }}>
            <option value="current">Current</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
            <option value="2023">2023</option>
            <option value="2022">2022</option>
            <option value="2021">2021</option>
          </select>
          </label>
          <a href={`${API_BASE}/analytics/warranty-ratio/history/pdf`} target="_blank" rel="noopener noreferrer" className="ni-button ni-button--ghost" style={{ textDecoration: 'none', fontSize: '0.9rem' }}>Download PDF</a>
        </div>
      </div>
      <div style={{ padding: '1rem 1.15rem', color: colors.textBody }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: colors.textStrong }}>
          <span>Total RMAs</span>
          <strong>{stats.total}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <span>Warranty Ratio (target &lt; 0.2%)</span>
          <strong style={{ color: getWarrantyRatioColor(warranty.ratio) }}>
            {(warranty.ratio * 100).toFixed(2)}%
          </strong>
        </div>
        <div style={{ textAlign: 'right', marginBottom: '0.9rem', fontSize: '0.9rem', color: colors.textMuted }}>
          {warranty.rmaCount} / {warranty.deviceCount}
        </div>
        <div style={{ fontSize: '0.95rem', marginBottom: '1rem', display: 'grid', gap: '0.25rem' }}>
          <div>Total RMAs this period: <strong>{warranty.totalRmas}</strong></div>
          <div>Accepted by NIN: <strong>{warranty.acceptedNinCount}</strong></div>
          <div>Accepted by Production Partner: <strong>{warranty.acceptedProdCount}</strong></div>
          <div>Non Warranty Claim: <strong>{warranty.nonWarrantyCount}</strong></div>
          <div>All-Time RMA Submissions: <strong>{warranty.allTimeRmaSubmissions}</strong></div>
          <div>All-Time Warranty Accepted by NIN: <strong>{warranty.allTimeAcceptedNin}</strong></div>
        </div>

        {stats.byWarrantyStatus && stats.byWarrantyStatus.length > 0 && (
          <section style={{ marginBottom: '1rem', padding: '0.9rem', borderRadius: '16px', backgroundColor: colors.surfaceRaised, border: `1px solid ${colors.panelBorder}` }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: colors.textStrong }}>Warranty Decisions</h4>
            <BarChart data={stats.byWarrantyStatus} />
          </section>
        )}

        <section style={{ marginBottom: '1rem', padding: '0.9rem', borderRadius: '16px', backgroundColor: colors.surfaceRaised, border: `1px solid ${colors.panelBorder}` }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: colors.textStrong }}>Submissions Over Time</h4>
          <BarChart data={stats.submissionsOverTime} />
        </section>

        <section style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 140px', padding: '0.9rem', borderRadius: '16px', backgroundColor: colors.surfaceRaised, border: `1px solid ${colors.panelBorder}` }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: colors.textStrong }}>By Issue Type</h4>
            <DoughnutChart data={stats.byIssue} colorMap={{}} />
            <ul style={{ listStyle: 'none', padding: 0, marginTop: '0.5rem', color: colors.textMuted }}>
              {stats.byIssue.map(item => (
                <li key={item._id}>{item._id}: {item.count}</li>
              ))}
            </ul>
          </div>
          {safeReturnReasonStats.length > 0 && (
            <div style={{ flex: '1 1 140px', padding: '0.9rem', borderRadius: '16px', backgroundColor: colors.surfaceRaised, border: `1px solid ${colors.panelBorder}` }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: colors.textStrong }}>By Return Reason</h4>
              <DoughnutChart data={safeReturnReasonStats} colorMap={{}} />
              <ul style={{ listStyle: 'none', padding: 0, marginTop: '0.5rem', color: colors.textMuted }}>
                {safeReturnReasonStats.map(item => (
                  <li key={item._id}>{item._id}: {item.count}</li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section style={{ padding: '0.9rem', borderRadius: '16px', backgroundColor: colors.surfaceRaised, border: `1px solid ${colors.panelBorder}` }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: colors.textStrong }}>By Status</h4>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            {stats.byStatus.map(item => {
              const key = normalizeStatusKey(item._id);
              const label = STATUS_LABELS[key] || item._id;
              const swatchColor = statusColors[key] || colors.muted;
              return (
                <li key={item._id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <span
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: swatchColor,
                      display: 'inline-block'
                    }}
                  ></span>
                  <span style={{ color: colors.textMuted }}>{label} ({item.count})</span>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
}

